// src/components/Common/ImageUploader.js
import React, { useCallback, useState } from 'react';
import { 
  Box, 
  Typography, 
  LinearProgress, 
  Alert,
  Snackbar,
  IconButton
} from '@mui/material';
import { CloudUpload, Close } from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

const ImageUploader = ({ 
  onUpload, 
  buttonLabel = 'رفع الصور', 
  maxSize = 5242880, // 5MB
  maxFiles = 10,
  showProgress = true,
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejectionReasons = rejectedFiles.map(file => {
          const errors = file.errors.map(error => {
            switch (error.code) {
              case 'file-too-large':
                return 'الملف كبير جداً (الحد الأقصى 5 ميجابايت)';
              case 'file-invalid-type':
                return 'نوع الملف غير مدعوم';
              case 'too-many-files':
                return `عدد الملفات يتجاوز الحد المسموح (${maxFiles})`;
              default:
                return 'خطأ غير معروف';
            }
          });
          return `${file.file.name}: ${errors.join(', ')}`;
        });
        
        setError(`فشل في رفع بعض الملفات:\n${rejectionReasons.join('\n')}`);
        return;
      }

      // Filter valid files
      const validFiles = acceptedFiles.filter(
        (file) => 
          file.type.startsWith('image/') && 
          file.size <= maxSize
      );

      if (validFiles.length === 0) {
        setError('لم يتم العثور على ملفات صالحة للرفع');
        return;
      }

      if (validFiles.length !== acceptedFiles.length) {
        setError('بعض الملفات لم يتم قبولها. يرجى التأكد من أن الملفات هي صور وأقل من 5 ميجابايت.');
      }

      try {
        setUploading(true);
        setUploadProgress(0);
        setError(null);
        
        // Simulate progress for better UX
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev < 90) return prev + 10;
            return prev;
          });
        }, 200);

        await onUpload(validFiles);
        
        clearInterval(progressInterval);
        setUploadProgress(100);
        setSuccess(true);
        
        // Reset progress after success
        setTimeout(() => {
          setUploadProgress(0);
          setSuccess(false);
        }, 2000);
        
      } catch (error) {
        setError(`حدث خطأ أثناء رفع الصور: ${error.message}`);
        console.error('Upload error:', error);
      } finally {
        setUploading(false);
      }
    },
    [onUpload, maxSize, maxFiles]
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxSize,
    maxFiles,
    disabled: disabled || uploading
  });

  const handleCloseError = () => {
    setError(null);
  };

  const handleCloseSuccess = () => {
    setSuccess(false);
  };

  const getBorderColor = () => {
    if (isDragReject) return 'error.main';
    if (isDragActive) return 'primary.main';
    if (uploading) return 'info.main';
    return 'grey.400';
  };

  const getBackgroundColor = () => {
    if (isDragReject) return 'rgba(211, 47, 47, 0.04)';
    if (isDragActive) return 'rgba(25, 118, 210, 0.04)';
    if (uploading) return 'rgba(2, 136, 209, 0.04)';
    return 'transparent';
  };

  return (
    <Box sx={{ width: '100%', mt: 1, mb: 2 }}>
      <Box
        {...getRootProps()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3,
          border: '2px dashed',
          borderColor: getBorderColor(),
          borderRadius: 1,
          bgcolor: getBackgroundColor(),
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          opacity: disabled || uploading ? 0.6 : 1,
          '&:hover': {
            borderColor: disabled || uploading ? getBorderColor() : 'primary.main',
            bgcolor: disabled || uploading ? getBackgroundColor() : 'rgba(25, 118, 210, 0.04)'
          }
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload 
          sx={{ 
            fontSize: 40, 
            mb: 1, 
            color: isDragReject ? 'error.main' : 
                   isDragActive ? 'primary.main' : 
                   uploading ? 'info.main' : 'grey.500'
          }} 
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
          {uploading ? 'جاري الرفع...' : buttonLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold', textAlign: 'center', color: 'text.secondary' }}>
          {isDragReject ? 'ملفات غير مدعومة' :
           isDragActive ? 'اترك الملفات هنا' :
           uploading ? 'يرجى الانتظار...' :
           'اسحب الصور هنا أو انقر للاختيار'}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 'bold', textAlign: 'center', color: 'text.disabled', mt: 1 }}>
          (الحد الأقصى: 5 ميجابايت للصورة الواحدة، {maxFiles} صور كحد أقصى)
        </Typography>
      </Box>

      {/* Progress Bar */}
      {showProgress && uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            sx={{ height: 8, borderRadius: 1 }}
          />
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            {Math.round(uploadProgress)}%
          </Typography>
        </Box>
      )}

      {/* Error Snackbar */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseError} 
          severity="error" 
          sx={{ width: '100%' }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleCloseError}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar 
        open={success} 
        autoHideDuration={3000} 
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccess} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          تم رفع الصور بنجاح!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImageUploader;
